import { createReducer } from 'src/helpers/reducer';
import actionTypes from './actionTypes';
import { linesRequestKey } from '../searchParams';
import { RATE_CARD_SIDES } from '../constants';
import type { RateCardSideId } from '../constants';
import type {
  ILoadLinesRequestPayload,
  ILoadLinesSuccessPayload,
  IRateCardLineHeader,
  IRateCardLinesState,
  IRateCardShow,
  IRateCardViewState,
} from '../types';

const initLines: IRateCardLinesState = {
  data: null,
  headers: [],
  headersLoading: false,
  headersError: false,
  filters: [],
  sortableColumns: [],
  filteredTotal: 0,
  loading: false,
  error: false,
  side: RATE_CARD_SIDES.PAY,
  requestKey: '',
};

const initState: IRateCardViewState = {
  card: null,
  loading: false,
  error: false,
  notFound: false,
  publishedLineCount: null,
  publishedLineCountError: false,
  pollAbandoned: false,
  exportingSides: [],
  deleting: false,
  deleted: false,
  lines: initLines,
};

export default createReducer(initState, {
  [actionTypes.LOAD_CARD_REQUEST]: (state: IRateCardViewState): IRateCardViewState => ({
    ...state,
    loading: true,
    error: false,
    notFound: false,
  }),

  [actionTypes.LOAD_CARD_SUCCESS]: (
    state: IRateCardViewState,
    { payload }: { payload: IRateCardShow },
  ): IRateCardViewState => ({
    ...state,
    card: payload,
    loading: false,
    error: false,
    notFound: false,
  }),

  [actionTypes.LOAD_CARD_ERROR]: (state: IRateCardViewState): IRateCardViewState => ({
    ...state,
    loading: false,
    error: true,
    notFound: false,
  }),

  [actionTypes.LOAD_CARD_NOT_FOUND]: (state: IRateCardViewState): IRateCardViewState => ({
    ...state,
    loading: false,
    error: false,
    notFound: true,
  }),

  [actionTypes.POLL_START]: (state: IRateCardViewState): IRateCardViewState => ({ ...state, pollAbandoned: false }),

  [actionTypes.POLL_ABANDONED]: (state: IRateCardViewState): IRateCardViewState => ({
    ...state,
    pollAbandoned: true,
  }),

  [actionTypes.LOAD_LINE_COUNT_REQUEST]: (state: IRateCardViewState): IRateCardViewState => ({
    ...state,
    publishedLineCountError: false,
  }),

  [actionTypes.LOAD_LINE_COUNT_SUCCESS]: (
    state: IRateCardViewState,
    { payload }: { payload: { total: number } },
  ): IRateCardViewState => ({
    ...state,
    publishedLineCount: payload.total,
    publishedLineCountError: false,
  }),

  // Unknown stays unknown: guessing zero would render a published card as if it had never been
  // imported. The flag is what lets the section say so instead of showing an empty state.
  [actionTypes.LOAD_LINE_COUNT_ERROR]: (state: IRateCardViewState): IRateCardViewState => ({
    ...state,
    publishedLineCount: null,
    publishedLineCountError: true,
  }),

  [actionTypes.LOAD_LINE_HEADERS_REQUEST]: (
    state: IRateCardViewState,
    { payload }: { payload: { side: RateCardSideId } },
  ): IRateCardViewState => ({
    ...state,
    lines: {
      ...state.lines,
      // Pay and bill do not share columns, so the old side's must go with the old side's rows —
      // otherwise the faster of the two responses renders under the slower one's headings.
      headers: payload.side === state.lines.side ? state.lines.headers : [],
      headersLoading: true,
      headersError: false,
    },
  }),

  [actionTypes.LOAD_LINE_HEADERS_SUCCESS]: (
    state: IRateCardViewState,
    { payload }: { payload: { headers: IRateCardLineHeader[] } },
  ): IRateCardViewState => ({
    ...state,
    lines: { ...state.lines, headers: payload.headers, headersLoading: false, headersError: false },
  }),

  // No columns is a failure of its own: rows would render with nothing to render them in.
  [actionTypes.LOAD_LINE_HEADERS_ERROR]: (state: IRateCardViewState): IRateCardViewState => ({
    ...state,
    lines: { ...state.lines, headers: [], headersLoading: false, headersError: true },
  }),

  [actionTypes.LOAD_LINES_REQUEST]: (
    state: IRateCardViewState,
    { payload }: { payload: ILoadLinesRequestPayload },
  ): IRateCardViewState => {
    const sameQuery = linesRequestKey(payload) === state.lines.requestKey;
    const sameSide = payload.side === state.lines.side;

    return {
      ...state,
      lines: {
        ...state.lines,
        // Rows survive a refetch of the same query — a blip must not blank a working grid — and go when it
        // changed. Page 1's rows shown as page 2, or one side's under the other's tab, are the wrong answer.
        data: sameQuery ? state.lines.data : null,
        // The filter chips are seeded from the response's echoed `activeFilters`, so carrying the
        // old side's would silently apply them to the new one's first request.
        filters: sameSide ? state.lines.filters : [],
        side: payload.side,
        loading: true,
        error: false,
      },
    };
  },

  [actionTypes.LOAD_LINES_SUCCESS]: (
    state: IRateCardViewState,
    { payload }: { payload: ILoadLinesSuccessPayload },
  ): IRateCardViewState => ({
    ...state,
    lines: {
      ...state.lines,
      data: payload.records,
      filters: payload.filters,
      sortableColumns: payload.sortableColumns,
      filteredTotal: payload.filteredTotal,
      side: payload.side,
      requestKey: payload.requestKey,
      loading: false,
      error: false,
    },
  }),

  // `data` survives so a failed refetch does not blank a grid that was already showing rows;
  // `error` is what stops the grid claiming the filters matched nothing.
  [actionTypes.LOAD_LINES_ERROR]: (state: IRateCardViewState): IRateCardViewState => ({
    ...state,
    lines: { ...state.lines, loading: false, error: true },
  }),

  [actionTypes.EXPORT_LINES_REQUEST]: (
    state: IRateCardViewState,
    { payload }: { payload: { side: RateCardSideId } },
  ): IRateCardViewState => ({
    ...state,
    exportingSides: state.exportingSides.includes(payload.side)
      ? state.exportingSides
      : [...state.exportingSides, payload.side],
  }),

  [actionTypes.EXPORT_LINES_SETTLED]: (
    state: IRateCardViewState,
    { payload }: { payload: { side: RateCardSideId } },
  ): IRateCardViewState => ({
    ...state,
    exportingSides: state.exportingSides.filter(side => side !== payload.side),
  }),

  [actionTypes.DELETE_REQUEST]: (state: IRateCardViewState): IRateCardViewState => ({ ...state, deleting: true }),

  // `deleted` rather than a callback: the page navigates off state, the way it already does for a card
  // that turned out not to exist. A callback would fire whatever the user did while the request ran.
  [actionTypes.DELETE_SUCCESS]: (state: IRateCardViewState): IRateCardViewState => ({
    ...state,
    deleting: false,
    deleted: true,
  }),
  [actionTypes.DELETE_ERROR]: (state: IRateCardViewState): IRateCardViewState => ({ ...state, deleting: false }),

  [actionTypes.CLEAR_STORE]: () => initState,
});
